import { useState } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { api } from '@/lib/apiClient';
import { useToast } from '@/hooks/use-toast';
import { Search, ShieldCheck, Clock, CheckCircle2, XCircle, AlertCircle, Heart, MessageSquare } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { es } from 'date-fns/locale';
import type { AnonymousReport } from '@/types/legacy';
import {
  categoryLabels,
  severityLabels,
  statusLabels,
  contactTypeLabels,
} from '@/schemas/anonymousReportSchemas';

interface AnonymousReportTrackerProps {
  onNewReport: () => void;
}

const statusIcons: Record<string, React.ReactNode> = {
  pending: <Clock className="w-4 h-4" />,
  reviewing: <AlertCircle className="w-4 h-4" />,
  in_progress: <AlertCircle className="w-4 h-4" />,
  resolved: <CheckCircle2 className="w-4 h-4" />,
  closed: <XCircle className="w-4 h-4" />,
};

const statusColors: Record<string, string> = {
  pending: 'bg-yellow-500/10 text-yellow-600 border-yellow-500/30',
  reviewing: 'bg-blue-500/10 text-blue-600 border-blue-500/30',
  in_progress: 'bg-purple-500/10 text-purple-600 border-purple-500/30',
  resolved: 'bg-green-500/10 text-green-600 border-green-500/30',
  closed: 'bg-gray-500/10 text-gray-600 border-gray-500/30',
};

export function AnonymousReportTracker({ onNewReport }: AnonymousReportTrackerProps) {
  const { toast } = useToast();
  const [trackingCode, setTrackingCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [report, setReport] = useState<AnonymousReport | null>(null);
  const [notFound, setNotFound] = useState(false);

  const handleSearch = async () => {
    if (!trackingCode.trim()) {
      toast({
        title: 'Error',
        description: 'Ingresa un código de seguimiento',
        variant: 'destructive',
      });
      return;
    }

    setLoading(true);
    setNotFound(false);
    setReport(null);

    try {
      const { data, error } = await api
        .from('anonymous_reports')
        .select('*')
        .eq('tracking_code', trackingCode.trim().toUpperCase())
        .single();

      if (error || !data) {
        setNotFound(true);
        return;
      }

      setReport(data);
    } catch (error) {
      setNotFound(true);
    } finally {
      setLoading(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold flex items-center gap-2">
            <ShieldCheck className="w-5 h-5 text-primary" />
            Buzón Anónimo
          </h3>
          <p className="text-sm text-muted-foreground">
            Reporta temas sensibles de forma confidencial o consulta el estado de tu reporte
          </p>
        </div>
        <Button onClick={onNewReport} className="bg-gradient-magma">
          Nuevo Reporte Anónimo
        </Button>
      </div>

      {/* Buscador */}
      <Card className="p-4">
        <div className="flex gap-2">
          <div className="flex-1">
            <Input
              placeholder="Ingresa tu código de seguimiento (ej: AR-X7K9M2)"
              value={trackingCode}
              onChange={(e) => setTrackingCode(e.target.value.toUpperCase())}
              onKeyPress={handleKeyPress}
              className="font-mono"
            />
          </div>
          <Button onClick={handleSearch} disabled={loading}>
            {loading ? (
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
            ) : (
              <>
                <Search className="w-4 h-4 mr-2" />
                Consultar
              </>
            )}
          </Button>
        </div>
      </Card>

      {/* Resultado: No encontrado */}
      {notFound && (
        <Alert variant="destructive">
          <XCircle className="h-4 w-4" />
          <AlertDescription>
            No se encontró ningún reporte con el código <strong>{trackingCode}</strong>.
            Verifica que el código esté escrito correctamente.
          </AlertDescription>
        </Alert>
      )}

      {/* Resultado: Reporte encontrado */}
      {report && (
        <Card className="p-6 space-y-4">
          <div className="flex items-start justify-between">
            <div>
              <p className="text-sm text-muted-foreground">Código de seguimiento</p>
              <code className="text-xl font-mono font-bold text-primary">{report.tracking_code}</code>
            </div>
            <Badge className={`${statusColors[report.status]} border`}>
              {statusIcons[report.status]}
              <span className="ml-1">{statusLabels[report.status as keyof typeof statusLabels]}</span>
            </Badge>
          </div>

          <div className="grid grid-cols-2 gap-4 py-4 border-y">
            <div>
              <p className="text-sm text-muted-foreground">Categoría</p>
              <p className="font-medium">{categoryLabels[report.category as keyof typeof categoryLabels]}</p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Urgencia</p>
              <p className="font-medium">{severityLabels[report.severity as keyof typeof severityLabels]}</p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Enviado</p>
              <p className="font-medium">
                {formatDistanceToNow(new Date(report.created_at), { addSuffix: true, locale: es })}
              </p>
            </div>
            {report.resolved_at && (
              <div>
                <p className="text-sm text-muted-foreground">Resuelto</p>
                <p className="font-medium">
                  {formatDistanceToNow(new Date(report.resolved_at), { addSuffix: true, locale: es })}
                </p>
              </div>
            )}
          </div>

          <div>
            <p className="text-sm text-muted-foreground mb-1">Asunto</p>
            <p className="font-medium">{report.subject}</p>
          </div>

          {report.request_professional_contact && (
            <Alert className="border-pink-500/30 bg-pink-500/5">
              <Heart className="h-4 w-4 text-pink-500" />
              <AlertDescription className="text-pink-700 dark:text-pink-400">
                Solicitaste contacto con profesional de{' '}
                <strong>{contactTypeLabels[report.contact_type as keyof typeof contactTypeLabels]}</strong>.
                {report.status === 'pending' && ' Tu solicitud está siendo procesada.'}
              </AlertDescription>
            </Alert>
          )}

          {report.admin_response && (
            <Card className="p-4 bg-primary/5 border-primary/20">
              <div className="flex items-start gap-3">
                <MessageSquare className="w-5 h-5 text-primary mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-primary mb-1">Respuesta de la empresa:</p>
                  <p className="text-sm whitespace-pre-wrap">{report.admin_response}</p>
                </div>
              </div>
            </Card>
          )}

          {!report.admin_response && report.status === 'pending' && (
            <Alert>
              <Clock className="h-4 w-4" />
              <AlertDescription>
                Tu reporte está pendiente de revisión. Vuelve a consultar más tarde para ver actualizaciones.
              </AlertDescription>
            </Alert>
          )}
        </Card>
      )}

      {/* Info cuando no hay búsqueda */}
      {!report && !notFound && (
        <Card className="p-8 text-center border-dashed">
          <ShieldCheck className="w-16 h-16 mx-auto text-muted-foreground mb-4" />
          <h4 className="font-semibold mb-2">Tu privacidad está protegida</h4>
          <p className="text-sm text-muted-foreground max-w-md mx-auto">
            Los reportes anónimos son completamente confidenciales.
            No almacenamos información que pueda identificarte.
            Solo tú puedes consultar el estado de tu reporte con el código único.
          </p>
        </Card>
      )}
    </div>
  );
}
